import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MousePointer, Hand, Settings, Palette, Type, LogIn, Zap } from 'lucide-react';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNeverShowAgain: () => void;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose, onNeverShowAgain }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  const slides = [
    {
      title: "Welcome to Pathways!",
      content: (
        <div className="space-y-4">
          <div className="text-center">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              🎯 Welcome to Pathways
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Connect synonyms in a perfect circle by finding words that can link together through meaning.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-blue-800">
              <strong>Goal:</strong> Find all the connections to complete the circle before running out of attempts!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "How to Play",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Game Instructions</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <span className="text-2xl">🎯</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Find Connections</h4>
                <p className="text-gray-600">Connect words that are synonyms or closely related in meaning.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-2 rounded-lg">
                <span className="text-2xl">⭕</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Complete the Circle</h4>
                <p className="text-gray-600">Each word connects to exactly 2 others, forming a perfect circle.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="bg-yellow-100 p-2 rounded-lg">
                <span className="text-2xl">🔥</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Limited Attempts</h4>
                <p className="text-gray-600">You have 5 attempts total. Each wrong guess adds an error circle in the center.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Control Modes",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Two Ways to Play</h3>
          </div>
          
          <div className="space-y-6">
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-3 mb-3">
                <Hand className="w-6 h-6 text-blue-600" />
                <h4 className="font-bold text-blue-800">Drag Mode (Default)</h4>
              </div>
              <p className="text-blue-700 mb-3">Click and drag from one word to another to create connections.</p>
              <div className="text-sm text-blue-600">Perfect for touch screens and intuitive interaction!</div>
            </div>
            
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
              <div className="flex items-center gap-3 mb-3">
                <MousePointer className="w-6 h-6 text-purple-600" />
                <h4 className="font-bold text-purple-800">Tap Mode</h4>
              </div>
              <p className="text-purple-700 mb-3">Click one word, then click another to connect them.</p>
              <div className="text-sm text-purple-600">Great for precise control and desktop use!</div>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
              <div className="flex items-center gap-2 text-gray-700">
                <Settings className="w-5 h-5" />
                <span className="font-medium">Switch modes anytime using the settings button in the top corner!</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Customize Your Experience",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Personalize Pathways</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-center gap-3 mb-2">
                <Type className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-800">Adjust Text Size</h4>
              </div>
              <p className="text-green-700">Choose from 5 different text sizes for comfortable reading.</p>
            </div>
            
            <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-5 h-5 text-orange-600" />
                <h4 className="font-semibold text-orange-800">Confirm Connections</h4>
              </div>
              <p className="text-orange-700">Enable confirmation dialogs to prevent accidental connections.</p>
            </div>
            
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-3 mb-2">
                <LogIn className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-800">Sign in with Google</h4>
              </div>
              <p className="text-blue-700">Save your progress and sync across devices.</p>
            </div>
          </div>
          
          <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
            <p className="text-gray-700 text-center">
              <Settings className="w-5 h-5 inline mr-2" />
              Access all these options in the <strong>Settings</strong> menu!
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleClose = () => {
    if (neverShowAgain) {
      onNeverShowAgain();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-6 text-white relative">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-bold">{slides[currentSlide].title}</h2>
              <div className="flex gap-2 mt-4">
                {slides.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === currentSlide ? 'bg-white w-8' : 'bg-white/40 w-2'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-8">
              <div className="min-h-[300px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {slides[currentSlide].content}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-gray-50 border-t">
              {currentSlide === slides.length - 1 && (
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={neverShowAgain}
                      onChange={(e) => setNeverShowAgain(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">Never show this tutorial again</span>
                  </label>
                </div>
              )}
              
              <div className="flex justify-between">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                <div className="flex gap-3">
                  {currentSlide < slides.length - 1 ? (
                    <button
                      onClick={nextSlide}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={handleClose}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Start Playing!
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TutorialModal;